import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import StatusBoardPage from "pages/dashboard/facility/StatusBoardPage";
// TODO: Virtual Queue + Virtual Registration disabled until Twilio BAA is in place
// import WaitingRoom from "components/queue/WaitingRoom";
import SchedulerPage from "pages/dashboard/facility/SchedulerPage";
import ClinicMessagingPage from "pages/dashboard/facility/ClinicMessagingPage";
import DepartmentsPage from "pages/dashboard/facility/DepartmentsPage";
import FacilityAvailabilityPage from "pages/dashboard/careverify/FacilityAvailabilityPage";
import FacilityAttestationDashboardPage from "pages/dashboard/careverify/FacilityAttestationDashboardPage";
import { SavoirCareBoardPage } from "features/savoirCare";
import InterpreterRequestsPage from "pages/dashboard/facility/InterpreterRequestsPage";
import CareStartStudioPage from "features/carestart/pages/CareStartStudioPage";
import ReadinessDashboard from "pages/dashboard/readiness/ReadinessDashboard";
import InstitutionReportsPage from "pages/dashboard/reports/InstitutionReportsPage";
import VideoLibraryPage from "pages/dashboard/institutionRollout/VideoLibraryPage";
import InstitutionBrandPage from "pages/dashboard/institutionRollout/InstitutionBrandPage";
import DistributePage from "pages/dashboard/institutionRollout/DistributePage";
// import VirtualRegistrationPage from "pages/dashboard/facility/VirtualRegistrationPage";
import Settings from "components/dashboard/Settings";
import ZipSlotBiddingPage from "pages/dashboard/zipSlotAds/ZipSlotBiddingPage";
import AdBalancePage from "pages/dashboard/zipSlotAds/AdBalancePage";
import AdPerformancePage from "pages/dashboard/zipSlotAds/AdPerformancePage";
import ZipSlotAdsGate from "components/common/ZipSlotAdsGate";
import UpgradePage from "pages/dashboard/UpgradePage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import { useFacility } from "hooks/useFacility";
import { useAuth } from "hooks/useAuth";
// import QueueDisplay from "components/queue/splitflap/QueueDisplay";
import DashboardLayout from "components/dashboard/DashboardLayout";
import FacilityOnboardingDialog from "components/dashboard/FacilityOnboardingDialog";
import FacilityChecklist from "components/dashboard/FacilityChecklist";
import { db } from "services/firebase";
import { PromptsManagerProvider } from "context/PromptsManager";

const DashboardRoutes = () => {
  const { data, setData } = useFacility();
  const { user, subscription, isGlobalAdmin } = useAuth();
  const location = useLocation();
  // const isBoard = location.pathname === "/dashboard/queue/board";
  // SAVOIR CARE renders as a full-width Kanban board (reuses the fullWidth layout hook).
  const isBoard = location.pathname === "/dashboard/savoircare";
  const [showFacilityOnboarding, setShowFacilityOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (
            userData?.role === "facility" &&
            !userData?.hideFacilityOnboardingDialog
          ) {
            setShowFacilityOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const handleCloseFacilityOnboarding = () => {
    setShowFacilityOnboarding(false);
  };

  return (
    <DashboardLayout fullWidth={isBoard}>
      {!isBoard && <FacilityChecklist locationData={data} />}

      <FacilityOnboardingDialog
        open={showFacilityOnboarding}
        onClose={handleCloseFacilityOnboarding}
        userId={user?.uid}
        locationData={data}
      />
      <PromptsManagerProvider>
        <Routes>
          <Route exact path="/" element={<StatusBoardPage data={data} />} />
          {/* TODO: Virtual Queue routes disabled until Twilio BAA is in place */}
          {/* <Route
            path="/queue/board"
            element={<QueueDisplay queue={data.queue} />}
          />
          <Route
            path="/virtual-registration"
            element={<VirtualRegistrationPage data={data} setData={setData} />}
          />
          <Route
            path="/queue"
            element={<WaitingRoom data={data} setData={setData} />}
          /> */}
          <Route
            path="/schedule"
            element={<SchedulerPage data={data} setData={setData} />}
          />
          <Route
            path="/careverify/availability"
            element={<FacilityAvailabilityPage data={data} />}
          />
          <Route
            path="/careverify/attestation"
            element={<FacilityAttestationDashboardPage data={data} />}
          />
          <Route
            path="/messaging"
            element={<ClinicMessagingPage data={data} setData={setData} />}
          />
          <Route
            path="/departments"
            element={<DepartmentsPage data={data} setData={setData} />}
          />
          <Route path="/savoircare" element={<SavoirCareBoardPage />} />
          <Route
            path="/interpreter-requests"
            element={<InterpreterRequestsPage />}
          />
          <Route path="/carestart-studio" element={<CareStartStudioPage />} />
          {/* App-launcher targets shown to the facility role (LoopManager,
              FusionLead) so their tiles resolve instead of bouncing home. */}
          <Route path="/readiness" element={<ReadinessDashboard />} />
          <Route path="/reports" element={<InstitutionReportsPage />} />
          <Route path="/institution-rollout" element={<VideoLibraryPage />} />
          <Route
            path="/institution-rollout/brand"
            element={<InstitutionBrandPage />}
          />
          <Route
            path="/institution-rollout/distribute"
            element={<DistributePage />}
          />
          <Route
            path="/settings"
            element={<Settings uid={user.uid} subscription={subscription} />}
          />
          <Route path="/upgrade" element={<UpgradePage />} />

          {/* Global-admin-only routes */}
          <Route
            path="/admin/approval"
            element={
              isGlobalAdmin ? (
                <ApprovalPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/assistants"
            element={
              isGlobalAdmin ? (
                <AssistantManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/usage"
            element={
              isGlobalAdmin ? (
                <UsageMonitorPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/prompts"
            element={
              isGlobalAdmin ? (
                <PromptsManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/advertising/slots"
            element={
              <ZipSlotAdsGate>
                <ZipSlotBiddingPage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/advertising/balance"
            element={
              <ZipSlotAdsGate>
                <AdBalancePage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/advertising/performance"
            element={
              <ZipSlotAdsGate>
                <AdPerformancePage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/advertising"
            element={<Navigate to="/dashboard/advertising/slots" replace />}
          />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default DashboardRoutes;
